import {
  ChevronFirst,
  ChevronLast,
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from "lucide-react";
import * as React from "react";

import {Button, buttonVariants} from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/sch-select";
import {cn} from "@/lib/utils";
import {ITEMS_PER_PAGE, PAGE_SIZE_OPTIONS} from "@/src/modules/core/utils/constants";

function SchPagination({className, ...props}: React.ComponentProps<"nav">) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn("mx-auto flex w-full justify-center", className)}
      {...props}
    />
  );
}

function PaginationContent({className, ...props}: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn("flex flex-row items-center gap-1", className)}
      {...props}
    />
  );
}

function PaginationItem({...props}: React.ComponentProps<"li">) {
  return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
  isActive?: boolean;
} & React.ComponentProps<typeof Button>;

function PaginationLink({className, isActive, size = "icon", ...props}: PaginationLinkProps) {
  return (
    <Button
      aria-current={isActive ? "page" : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      variant={isActive ? "default" : "ghost"}
      className={cn(
        buttonVariants({
          variant: isActive ? "default" : "ghost",
          size,
        }),
        className,
      )}
      {...props}
    />
  );
}

function PaginationPrevious({className, ...props}: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={cn("gap-1 px-2.5 sm:pl-2.5", className)}
      {...props}
    >
      <ChevronLeftIcon />
    </PaginationLink>
  );
}

function PaginationNext({className, ...props}: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={cn("gap-1 px-2.5 sm:pr-2.5", className)}
      {...props}
    >
      <ChevronRightIcon />
    </PaginationLink>
  );
}

function PaginationEllipsis({className, ...props}: React.ComponentProps<"span">) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn("flex size-9 items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontalIcon className="size-4" />
      <span className="sr-only">More pages</span>
    </span>
  );
}

function getPaginationItems(
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void,
) {
  const items = [];
  if (currentPage - 1 > 1) {
    items.push(
      <PaginationItem key={`pagination-ellipsis-${currentPage - 1}`}>
        <PaginationEllipsis />
      </PaginationItem>,
    );
  }

  if (currentPage > 1) {
    items.push(
      <PaginationItem key={`pagination-previous-${currentPage - 1}`}>
        <PaginationLink onClick={() => onPageChange(currentPage - 1)}>
          {currentPage - 1}
        </PaginationLink>
      </PaginationItem>,
    );
  }
  items.push(
    <PaginationItem key={`pagination-current-${currentPage}`}>
      <PaginationLink isActive>{currentPage}</PaginationLink>
    </PaginationItem>,
  );

  if (currentPage < totalPages) {
    items.push(
      <PaginationItem key={`pagination-next-${currentPage + 1}`}>
        <PaginationLink onClick={() => onPageChange(currentPage + 1)}>
          {currentPage + 1}
        </PaginationLink>
      </PaginationItem>,
    );
  }

  if (currentPage + 1 < totalPages) {
    items.push(
      <PaginationItem key={`pagination-ellipsis-${currentPage + 1}`}>
        <PaginationEllipsis />
      </PaginationItem>,
    );
  }
  return items;
}

function Pagination({
  page,
  from,
  to,
  total,
  pageSize = ITEMS_PER_PAGE,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  from: number;
  to: number;
  total: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const paginationItems = getPaginationItems(page, totalPages, onPageChange);
  return (
    <div className="bg-muted/50 text-muted-foreground flex flex-col gap-3 rounded-lg px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span>
          {from} to {to} of {total}
        </span>
        {onPageSizeChange ? (
          <Select
            value={String(pageSize)}
            onValueChange={(value) => {
              if (value) onPageSizeChange(Number(value));
            }}
          >
            <SelectTrigger
              size="sm"
              className="h-8 w-[4.5rem]"
              aria-label="Rows per page"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top" sideOffset={4}>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </div>
      <div className="ml-auto">
        <SchPagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationLink disabled={page === 1} onClick={() => onPageChange(1)}>
                <ChevronFirst className="size-4" />
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationPrevious disabled={page === 1} onClick={() => onPageChange(page - 1)} />
            </PaginationItem>
            {paginationItems}
            <PaginationItem>
              <PaginationNext
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink
                disabled={page >= totalPages}
                onClick={() => onPageChange(totalPages)}
              >
                <ChevronLast className="size-4" />
              </PaginationLink>
            </PaginationItem>
          </PaginationContent>
        </SchPagination>
      </div>
    </div>
  );
}

export {
  Pagination,
  SchPagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
